const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Domain to Organization mapping
 * Maps specific domains to their organization slugs/IDs
 */
const DOMAIN_ORG_MAPPING: Record<string, string> = {
  'scores.stuttgart-padel.com': 'spl',
  'localhost:3000': 'spl', // Default for development
  'localhost': 'spl',
  'padel-eagle.localhost:3000': 'padel-eagle',
  'padel-eagle.localhost': 'padel-eagle',
  // Add Padel Eagle production domain when ready:
  // 'scores.padel-eagle.com': 'padel-eagle',
};

/**
 * Get organization ID based on current domain
 */
const getOrgFromDomain = (): string => {
  const hostname = window.location.hostname;
  const hostnameWithPort = window.location.host; // includes port
  
  console.log('[API Config] Current hostname:', hostnameWithPort);
  
  // Check exact match first (with port)
  if (DOMAIN_ORG_MAPPING[hostnameWithPort]) {
    console.log('[API Config] Mapped to org:', DOMAIN_ORG_MAPPING[hostnameWithPort]);
    return DOMAIN_ORG_MAPPING[hostnameWithPort];
  }
  
  // Check hostname without port
  if (DOMAIN_ORG_MAPPING[hostname]) {
    console.log('[API Config] Mapped to org:', DOMAIN_ORG_MAPPING[hostname]);
    return DOMAIN_ORG_MAPPING[hostname];
  }
  
  // Default to SPL
  console.log('[API Config] Using default org: spl');
  return 'spl';
};

/**
 * Random per-browser ID, persisted in localStorage (not a cookie - the
 * frontend and backend are on different domains, so a cookie set by the
 * backend would be third-party and get blocked by Safari/Firefox). Sent as
 * X-Device-Id on every request so a flagged account can still be recognized
 * (and its new IP auto-blocked) even when browsing anonymously or rotating
 * IPs, as long as it's the same browser/localStorage - see
 * padelBackend/src/utils/deviceBlocklist.ts.
 */
// crypto.randomUUID() is missing on older phones (iOS < 15.4, old Android WebViews) and
// localStorage can throw (Safari private mode, blocked storage). Either one used to make
// EVERY API call throw, which left those visitors on a blank page, so never let this fail.
const generateId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    // fall through to the weak fallback below
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`;
};

let inMemoryDeviceId: string | null = null;

const getOrCreateDeviceId = (): string => {
  const STORAGE_KEY = 'deviceId';
  try {
    let deviceId = localStorage.getItem(STORAGE_KEY);
    if (!deviceId) {
      deviceId = generateId();
      localStorage.setItem(STORAGE_KEY, deviceId);
    }
    return deviceId;
  } catch {
    // Storage unavailable: keep one ID for the lifetime of the page instead
    inMemoryDeviceId = inMemoryDeviceId || generateId();
    return inMemoryDeviceId;
  }
};

console.log('[API Config] Environment:', process.env.NODE_ENV);
console.log('[API Config] API Base URL:', API_BASE_URL);
console.log('[API Config] Organization from domain:', getOrgFromDomain());

/**
 * Resolve the current organization ID: localStorage dev-switcher override
 * takes priority, falling back to the domain mapping.
 */
export const getOrgId = (): string => {
  const domainOrgId = getOrgFromDomain();
  const currentOrgStr = localStorage.getItem('currentOrganization');
  if (currentOrgStr) {
    try {
      const currentOrg = JSON.parse(currentOrgStr);
      // Use the slug, not the Firebase document ID
      return currentOrg.slug || currentOrg.id || domainOrgId;
    } catch (e) {
      console.warn('[API] Failed to parse currentOrganization from localStorage');
    }
  }
  return domainOrgId;
};

export { getOrCreateDeviceId };

export const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  // Check for both player and admin tokens
  const adminToken = localStorage.getItem('authToken');
  const playerToken = localStorage.getItem('playerAuthToken');
  const isPlayerRoute = window.location.pathname.startsWith('/player');
  const isPlayerEndpoint = endpoint.startsWith('/api/player');
  const preferPlayerToken = isPlayerRoute || isPlayerEndpoint;

  // In player views (including admin impersonation), prefer player auth token.
  // In admin views, keep preferring admin token.
  const token = preferPlayerToken
    ? (playerToken || adminToken)
    : (adminToken || playerToken);

  const orgId = getOrgId();

  console.log(`[API Request] ${endpoint} - Org: ${orgId}`);
  
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Organization-Id': orgId,
      'X-Device-Id': getOrCreateDeviceId(),
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  });

  // Check if the response is 401 or 403 (token expired or invalid)
  if (response.status === 401 || response.status === 403) {
    // Clone the response so we can read it here AND let the caller read it too
    const clonedResponse = response.clone();
    
    let errorData: any = {};
    try {
      errorData = await clonedResponse.json();
    } catch (e) {
      // If we can't parse the error, treat it as token expired
      errorData = { error: 'Invalid or expired token' };
    }

    // Blocked IP: send the visitor to the normal login page instead of leaving
    // a half-loaded page with empty data. Do this even with no token present -
    // most hits will be on public/homepage endpoints with nobody logged in.
    if (errorData.code === 'ACCESS_BLOCKED') {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      localStorage.removeItem('loginTime');
      localStorage.removeItem('playerAuthToken');
      localStorage.removeItem('playerAuthUser');
      localStorage.removeItem('playerLoginTime');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      throw new Error('Access unavailable.');
    }

    // Check if it's a token-related error
    const tokenErrors = [
      'Token expired',
      'Invalid token',
      'Invalid or expired token',
      'No token provided',
      'Access token required',
      'This account belongs to a different organization'
    ];
    
    const isTokenError = tokenErrors.some(err => 
      errorData.error?.includes(err) || errorData.message?.includes(err)
    );
    
    if (isTokenError || response.status === 403) {
      // Determine which token was used for this specific request.
      const isPlayerSession = token === playerToken && !!playerToken;
      
      console.log('[API] Token expired or invalid, logging out:', {
        isPlayerSession,
        status: response.status,
        error: errorData.error
      });
      
      // Clear auth data based on user type
      if (isPlayerSession) {
        localStorage.removeItem('playerAuthToken');
        localStorage.removeItem('playerAuthUser');
        localStorage.removeItem('playerLoginTime');
        sessionStorage.setItem('sessionExpired', 'true');
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/player/login')) {
          window.location.href = '/player/login';
        }
      } else if (adminToken) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        localStorage.removeItem('loginTime');
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
      
      // Throw an error to stop further processing
      throw new Error('Session expired. Please login again.');
    }
  }

  return response;
};

export { API_BASE_URL };